import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';

import Analytics from '../../../analytics/Analytics';
import Ad from '../../../models/banner/Ad';
import { modelOf } from '../../../prop-types';
import ConfigStore from '../../../store/ConfigStore';
import UIStore from '../../../store/UIStore';
import AdZones from '../../../types/AdZones';
import LinkType from '../../../types/LinkType';
import { ViewBreakpointMinWidth } from '../../../types/ViewBreakpoint';
import BannerType from '../../../types/banner/BannerType';
import BannerVideoType from '../../../types/banner/BannerVideoType';
import MaybeExternalLink from '../../common/MaybeExternalLink';
import ProductCode from '../../product/ProductCode';
import ProductPrice from '../../product/ProductPrice';
import ProductImageSign from '../../product/ProductSign';
import ProductSignDiscount from '../../product/ProductSignDiscount';
import ProductSigns from '../../product/ProductSigns';
import { getBannerImageDimensions } from '../bannerMedia';
import useBannerInteractions from './hook';

const Banner = ({
  configStore,
  uiStore,
  analytics,
  banner,
  bannerZone,
  className,
  onVideoPlaybackChange,
  onImageMeasured,
}) => {
  const [handleInteraction, handleInteractionDown] = useBannerInteractions();
  const imageRef = useRef(null);

  const useMobileImage = uiStore.isSmallest && !!banner.mobile_image;
  const apiImageDimensions = getBannerImageDimensions(banner, useMobileImage);
  const needsIntrinsicMeasure =
    (!!banner.image || !!banner.mobile_image) &&
    banner.type !== BannerType.VIDEO &&
    (!apiImageDimensions.width || !apiImageDimensions.height);

  const reportMeasuredSize = (img) => {
    if (!needsIntrinsicMeasure || !onImageMeasured || !img) {
      return;
    }
    onImageMeasured({ bannerId: banner.id, img });
  };

  useEffect(() => {
    const img = imageRef.current;
    if (img?.complete) {
      reportMeasuredSize(img);
    }
    // Cached images may skip onLoad; re-check when src / API dims change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    banner.id,
    banner.image,
    banner.mobile_image,
    useMobileImage,
    needsIntrinsicMeasure,
  ]);
  const sendPromotionClick = () => {
    analytics.selectPromotion([
      {
        bannerZone,
        banner,
      },
    ]);
  };

  const handleBannerClick = (event) => {
    if (event.target.closest('video')) {
      return;
    }

    sendPromotionClick();
  };

  const renderBannerWrapper = () => {
    const bannerWrapper = uiStore.isSmallest
      ? 'Banner__wrapper-mobile'
      : 'Banner__wrapper';

    const type = banner.is_external
      ? LinkType.EXTERNAL_LINK
      : LinkType.INTERNAL_LINK;

    const Wrapper = ({ children }) => (
      <div className={bannerWrapper}>
        {banner.link_url ? (
          <MaybeExternalLink
            accessible={false}
            to={banner.link_url}
            type={type}
            onMouseDown={handleInteractionDown}
            onTouchStart={handleInteractionDown}
            onClick={handleInteraction}
          >
            {children}
          </MaybeExternalLink>
        ) : (
          children
        )}
      </div>
    );
    return (
      <Wrapper>
        {renderContent()}
        {renderBannerOverlay()}
      </Wrapper>
    );
  };

  const renderContent = () => {
    return banner.type === BannerType.VIDEO ? renderVideo() : renderImage();
  };

  const isFileVideo = () =>
    banner.type === BannerType.VIDEO &&
    banner.video_embed_style === BannerVideoType.FILE;

  const isPortraitFileVideo = () =>
    isFileVideo() &&
    uiStore.isSmallest &&
    banner.video_poster_height > banner.video_poster_width;

  const getFileVideoAspectRatio = () => {
    if (!banner.video_poster_width || !banner.video_poster_height) {
      return undefined;
    }

    return `${banner.video_poster_width} / ${banner.video_poster_height}`;
  };

  const renderVideo = () => {
    const fileVideo = isFileVideo();

    return (
      <div
        className={classNames('Banner__video-container', {
          'Banner__video-container--file': fileVideo,
        })}
        style={
          fileVideo ? { aspectRatio: getFileVideoAspectRatio() } : undefined
        }
      >
        {fileVideo ? renderVideoFile() : renderEmbedMedia()}
      </div>
    );
  };

  const emitVideoPlaybackChange = (isPlaying) => {
    onVideoPlaybackChange?.({ bannerId: banner.id, isPlaying });
  };

  const renderVideoFile = () => {
    return (
      <video
        controls
        loop
        preload="none"
        poster={banner.video_poster}
        width={banner.video_poster_width}
        height={banner.video_poster_height}
        onClick={(event) => event.stopPropagation()}
        onPlay={() => emitVideoPlaybackChange(true)}
        onPause={() => emitVideoPlaybackChange(false)}
        onEnded={() => emitVideoPlaybackChange(false)}
      >
        <source src={banner.video_file} type={banner.video_mime_type} />
      </video>
    );
  };

  const renderEmbedMedia = () => {
    return (
      <iframe src={banner.video_link} loading="lazy" frameBorder="0"></iframe>
    );
  };

  const renderImage = () => {
    const imageSrc = useMobileImage ? banner.mobile_image : banner.image;
    const { width: imageWidth, height: imageHeight } = apiImageDimensions;
    const aspectRatio =
      imageWidth && imageHeight
        ? `auto ${imageWidth} / ${imageHeight}`
        : undefined;

    return (
      <picture>
        {banner.mobile_image && (
          <source
            media={`(max-width:${ViewBreakpointMinWidth.SM}px)`}
            srcSet={encodeURI(banner.mobile_image)}
          />
        )}
        <img
          ref={imageRef}
          className={
            useMobileImage
              ? 'Banner__background-mobile-image'
              : 'Banner__background-image'
          }
          src={imageSrc ? encodeURI(imageSrc) : undefined}
          alt={banner.seo_description}
          style={{
            aspectRatio,
          }}
          width={imageWidth || undefined}
          height={imageHeight || undefined}
          onLoad={(event) => reportMeasuredSize(event.currentTarget)}
        />
      </picture>
    );
  };

  const renderBannerOverlay = () => {
    const title = getBannerTitle(banner);
    const content = getBannerContent(banner);
    const wrapperAlign = banner.stylesAlign;
    const priceInformation = getBannerPriceInformation(banner);

    return (
      (title || content) && (
        <>
          <div
            className="Banner__overlay-background"
            style={{ background: banner.overlay_background_color }}
          />
          <div
            className="Banner__overlay"
            style={{
              color: banner.text_color || undefined,
              pointerEvents:
                banner.type === BannerType.VIDEO ? 'none' : undefined,
              textAlign: wrapperAlign.textAlign || undefined,
            }}
          >
            {banner.overlay_image && (
              <img src={banner.overlay_image} alt={banner.seo_description} />
            )}
            {getBannerContentWrapper(
              title,
              banner,
              priceInformation,
              content,
              wrapperAlign
            )}
          </div>
        </>
      )
    );
  };

  const getBannerTitle = (banner) => {
    if (!banner.title) return;

    return (
      <div className="Banner__title">
        {banner.manufacturer && (
          <div className="Banner__manufacturer">{banner.manufacturer}</div>
        )}
        <h3 style={banner.stylesTitle(uiStore.isSmallest)}>{banner.title}</h3>
      </div>
    );
  };

  const getBannerContent = (banner) => {
    if (!banner.content) return;

    return (
      <div
        className="Banner__content"
        style={banner.stylesContent(uiStore.isSmallest)}
      >
        {banner.content}
      </div>
    );
  };

  const getBannerDiscountText = (banner) =>
    banner.discount_text ? (
      <div className="Banner__discount-text">
        <ProductImageSign>
          <ProductSignDiscount discountText={banner.discount_text} />
        </ProductImageSign>
      </div>
    ) : null;

  const getBannerPriceInformation = (banner) =>
    banner.price_info ? (
      <div className="Banner__price-info">
        <ProductPrice priceInfo={banner.price_info} />
        <ProductSigns
          hasDiscount={banner.price_info.is_discount}
          dateAdded={banner.date_added}
          discountProps={{
            percent: banner.price_info.discount_percentage,
            withUpTo: false,
            singlePriceLabel: false,
          }}
        />
      </div>
    ) : (
      getBannerDiscountText(banner)
    );

  const getBannerContentWrapper = (
    title,
    banner,
    priceInformation,
    content,
    style
  ) => (
    <div
      className="Banner__content_wrapper"
      style={{
        justifyContent: style?.justifyContent,
      }}
    >
      <div className="Banner__texts">
        {title}
        {banner.product_id && <ProductCode code={banner.productCode} />}
        {priceInformation}
        {content}
      </div>
    </div>
  );

  const shouldShowScent = () => {
    return (
      (bannerZone === AdZones.MAIN_BANNER ||
        bannerZone === AdZones.FOOTER_BANNER) &&
      !!configStore.banner.scent.pattern &&
      (!hasVideo() || (hasVideo() && !configStore.banner.scent.onTop)) &&
      !(uiStore.isMobile && !configStore.banner.scent.shouldShowMobile)
    );
  };

  const getBannerScent = () => {
    let onTop = {};

    if (!shouldShowScent()) {
      return null;
    }

    if (configStore.banner.scent.onTop) {
      onTop.position = 'relative';
      onTop.top = '-50px';
    }
    onTop.backgroundPositionX = configStore.banner.scent.positionX;
    onTop.backgroundRepeat = configStore.banner.scent.repeat;

    return (
      <div
        className="Banner__scent"
        style={{
          backgroundImage: `url(${configStore.banner.scent.pattern})`,
          ...onTop,
        }}
      />
    );
  };

  const hasVideo = () => {
    return (
      banner.type === BannerType.VIDEO &&
      (!!banner.video_file || !!banner.video_link)
    );
  };

  const hasMedia = () => {
    const hasImage = !!banner.image || !!banner.mobile_image;
    return hasImage || hasVideo();
  };

  return (
    <div
      className={classNames(
        'Banner',
        {
          'Banner--default-width': !hasMedia(),
          'Banner--file-video': isFileVideo(),
          'Banner--file-video-portrait': isPortraitFileVideo(),
          'Banner--text-color-override': !!banner.text_color,
        },
        className
      )}
      data-banner-id={banner.id}
      onClick={handleBannerClick}
    >
      {renderBannerWrapper()}
      {getBannerScent()}
    </div>
  );
};

Banner.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  banner: modelOf(Ad).isRequired,
  bannerZone: PropTypes.string.isRequired,
  className: PropTypes.string,
  onVideoPlaybackChange: PropTypes.func,
  onImageMeasured: PropTypes.func,
};

export default inject('configStore', 'uiStore', 'analytics')(observer(Banner));
